import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../hooks/useLanguage';
import { SunIcon, MoonIcon } from './icons';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  /** Compact variant used in the mobile header row. */
  compact?: boolean;
  className?: string;
}

/**
 * Day / dusk switch. The two icons are stacked and cross-faded with a small
 * rotation so the sun "sets" into a moon — a quiet nod to the Vas dawn-to-dusk
 * palette. Animation is disabled under prefers-reduced-motion.
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({ compact = false, className }) => {
  const { resolvedTheme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const isDark = resolvedTheme === 'dark';

  const label = isDark
    ? t('header.theme.toLight')
    : t('header.theme.toDark');
  const size = compact ? 'w-8 h-8' : 'w-9 h-9';
  const iconSize = compact ? 'w-4 h-4' : 'w-[18px] h-[18px]';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={cn(
        'relative inline-flex items-center justify-center rounded-full border border-border',
        'text-stone-600 hover:text-primary hover:bg-stone-50',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        size,
        className
      )}
      aria-label={label}
      title={label}
    >
      <span className="sr-only">{label}</span>
      {/* Sun — visible in light mode */}
      <SunIcon
        aria-hidden="true"
        className={cn(
          'absolute transition-all duration-300 ease-out motion-reduce:transition-none',
          iconSize,
          isDark ? 'opacity-0 -rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'
        )}
      />
      {/* Moon — visible in dark mode */}
      <MoonIcon
        aria-hidden="true"
        className={cn(
          'absolute transition-all duration-300 ease-out motion-reduce:transition-none',
          iconSize,
          isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 rotate-90 scale-50'
        )}
      />
    </button>
  );
};

export default ThemeToggle;
